import React, { useState, useEffect, useRef } from 'react';
import LinkButton from './LinkButton';
import { useModels, modelKey, ModelPicker, PromptLink } from './Pipeline/modelInfo';
import StableLabel from './StableLabel';
import { collapseNewlinesForTts } from '../utils/ttsText';
import { useTtsPreview, requestPreviewText } from '../utils/ttsPreview';
import { notifyTtsAttempt } from './TtsAttemptsChip';

// Prebuilt Gemini TTS voice names (as listed in AI Studio).
const PREBUILT_VOICES = [
  'Zephyr', 'Puck', 'Charon', 'Kore', 'Fenrir', 'Leda', 'Orus', 'Aoede',
  'Callirrhoe', 'Autonoe', 'Enceladus', 'Iapetus', 'Umbriel', 'Algieba',
  'Despina', 'Erinome', 'Algenib', 'Rasalgethi', 'Laomedeia', 'Achernar',
  'Alnilam', 'Schedar', 'Gacrux', 'Pulcherrima', 'Achird', 'Zubenelgenubi',
  'Vindemiatrix', 'Sadachbia', 'Sadaltager', 'Sulafat',
];

// TTS models the Gemini API lists (GET /v1beta/models, filtered by "tts") —
// the Model field offers them as a picklist; any other id can still be typed.
export const TTS_MODELS = [
  'gemini-3.1-flash-tts-preview',
  'gemini-3.8-flash-tts',
  'gemini-3.8-flash-lite-tts',
  'gemini-2.5-flash-preview-tts',
  'gemini-2.5-pro-preview-tts',
];

// 3.1 is the default again (15.10.2026: it reads better than 3.8). From
// 01.10 to 15.10 every settings object loaded with 3.1 was switched to 3.8
// on load and saved back that way, so a stored 3.8 nobody picked by hand is
// indistinguishable from the default of that time: such a save (no
// `defaultsRev`) goes back to 3.1. A model picked by hand or restored from a
// take's record carries `modelPinned` and is kept as is.
const DEFAULTS_REV = 2;
const SWITCHED_DEFAULT_MODEL = 'gemini-3.8-flash-tts';
const upgradeTtsModel = (s) => {
  if (!s || s.defaultsRev >= DEFAULTS_REV) return s;
  const model = s.model === SWITCHED_DEFAULT_MODEL && !s.modelPinned ? DEFAULT_SETTINGS.model : s.model;
  return { ...s, model, defaultsRev: DEFAULTS_REV };
};

// Defaults mirror the AI Studio export (ai_studio_code (2).py).
// Exported: the voiceover batch panel reuses these settings for its TTS step.
export const DEFAULT_SETTINGS = {
  model: 'gemini-3.1-flash-tts-preview',
  // How a take goes out — see TTS_SCHEMES.
  scheme: 'turns',
  temperature: 1,
  voice1: 'Charon',
  voice2: 'Kore',
  profile1: 'A friend who knows this subject well and is genuinely into it — engaging but natural, just talking to you and sharing what he finds interesting.',
  profile2: 'Interested in the subject and enjoys sharing it — casual and engaged, the way someone talks when they actually find something fascinating.',
  note1: 'Style: The "Vocal Smile": The soft palate is raised to keep the tone bright, sunny, and explicitly inviting. Pace: Fast, energetic, no dead air. Sentences overlap slightly. Accent: Neutral.',
  note2: 'Style: The "Vocal Smile": The soft palate is raised to keep the tone bright, sunny, and explicitly inviting. Pace: Fast, energetic, no dead air. Sentences overlap slightly. Accent: Neutral.',
  scene: 'Two friends who both know this subject well and are into it, trading off telling the story.',
  sampleContext: 'Like a friend explaining something he finds genuinely fascinating over coffee',
};

// How a take goes to Google. 'turns' is what 3.1 needed: one request per
// "Speaker N:" turn with that turn's voice in BOTH slots, the pieces glued
// here — with two voices in one request 3.1 lost a speaker about one take in
// three and the other voice drifted. 'whole' sends the take as one request
// with both voices (one quota unit instead of one per turn); whether 3.8
// keeps the voices apart that way is what a run with it is for.
export const TTS_SCHEMES = [
  { value: 'turns', label: 'One request per turn (the turn’s voice in both slots, glued here)' },
  { value: 'whole', label: 'Whole take in one request (both voices, no gluing)' },
];
export const schemeLabel = (v) => ((TTS_SCHEMES.find((s) => s.value === v) || TTS_SCHEMES[0]).label);

// What each speaker's style says in the 3.8 request shape, where the model
// reads the text verbatim and takes delivery instructions only from a
// per-turn style: the speaker's profile and director's note, then the scene
// and the sample context the prompt used to carry for both.
export const buildSpeakerStyles = (s) => {
  const shared = [s.scene && `Scene: ${s.scene}`, s.sampleContext && `Sample context: ${s.sampleContext}`];
  const join = (xs) => xs.map((x) => (x || '').trim()).filter(Boolean).join(' ');
  return {
    speaker1: join([s.profile1, s.note1, ...shared]),
    speaker2: join([s.profile2, s.note2, ...shared]),
  };
};

// The key the panel starts with; the studio's one-click "new take" uses it too.
// Nothing secret ships to the browser any more: with no key in the request
// the server picks one from its own pool (and rotates off a rate-limited
// one). A key typed into the panel is still honoured as given.
export const DEFAULT_TTS_API_KEY = '';

export const PRESETS_STORAGE_KEY = 'speechGenPresets';
export const LAST_SETTINGS_STORAGE_KEY = 'speechGenLastSettings';

const loadStoredJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return Array.isArray(fallback) ? parsed : { ...fallback, ...parsed };
  } catch (e) {
    console.error(`Failed to load ${key} from localStorage:`, e);
    return fallback;
  }
};

// Assemble the same prompt AI Studio builds from its separate UI fields.
// The server appends the transcript right after this block (`prompt\ntext`),
// so it has to end with the "## Transcript:" header.
export const buildSystemPrompt = (s) => (
  `Read the following transcript based on the audio profile and director's note.

# Audio Profile
For Speaker 1: ${s.profile1}
For Speaker 2: ${s.profile2}

# Director's note
For Speaker 1: ${s.note1}
For Speaker 2: ${s.note2}

## Scene:
${s.scene}

## Sample Context:
${s.sampleContext}

## Transcript:`
);

// The settings the panel last generated with — voices, model, temperature and
// the director's notes. A one-click take from elsewhere reuses exactly these.
export const loadLastTtsSettings = () => upgradeTtsModel(loadStoredJson(LAST_SETTINGS_STORAGE_KEY, DEFAULT_SETTINGS));

// A take's recorded settings (tts_configs.config, migration 028) made the
// last-used ones, so the next take — from the panel or the studio — goes out
// the same way. Needs the panel's own fields: the prompt and styles were
// glued from them. -> the settings stored, or null when the record has none.
export const applyTtsConfigAsLast = (config) => {
  if (!config || !config.panel) return null;
  const next = {
    ...DEFAULT_SETTINGS,
    ...config.panel,
    model: config.model,
    scheme: config.scheme,
    temperature: config.temperature,
    voice1: config.voices.speaker1,
    voice2: config.voices.speaker2,
    modelPinned: true,
  };
  localStorage.setItem(LAST_SETTINGS_STORAGE_KEY, JSON.stringify(next));
  return next;
};

// Saved named presets, each with its model id upgraded off the legacy default.
export const loadTtsPresets = () => {
  const presets = loadStoredJson(PRESETS_STORAGE_KEY, {});
  return Object.fromEntries(Object.entries(presets).map(([k, v]) => [k, upgradeTtsModel(v)]));
};

// Body for /api/generate-speech-task. `activate: false` stores the take in the
// history without replacing the one the app plays.
export const buildTtsRequest = ({ text, settings, apiKey, locationUuid, fieldType, questId, activate = true }) => ({
  text: collapseNewlinesForTts(text),
  ...(apiKey ? { apiKey } : {}),
  model: settings.model,
  scheme: settings.scheme === 'whole' ? 'whole' : 'turns',
  system_prompt: buildSystemPrompt(settings),
  styles: buildSpeakerStyles(settings),
  temperature: settings.temperature,
  voices: { speaker1: settings.voice1, speaker2: settings.voice2 },
  // The panel's own fields: the prompt and styles above are glued from them
  // and can't be taken apart, so the take's record keeps them as typed.
  panel: settings,
  locationUuid,
  fieldType,
  questId,
  activate,
});


const SpeechGeneration = ({ initialData, onBack, backHref, onSaveVoiceoverText, onAudioUploaded }) => {
  // Which model each text pass runs on — named on the buttons below.
  const pipelineModels = useModels();
  // Accent characters for easy copying
  const accentChars = ['А́', 'а́', 'Е́', 'е́', 'И́', 'и́', 'О́', 'о́', 'У́', 'у́', 'Ы́', 'ы́', 'Э́', 'э́', 'Ю́', 'ю́', 'Я́', 'я́'];
  const emojiChars = ['😀', '😂', '😍', '🤔', '👍', '🙏', '🎉', '🔥', '❤️', '👋'];
  const insertSnippets = ['Speaker 1: ', 'Speaker 2: ', '[firmly] '];

  // State for the form and generated audio
  const [text, setText] = useState('');
  const [locationId, setLocationId] = useState(null);
  const [locationUuid, setLocationUuid] = useState(null);
  const [field, setField] = useState(null);
  const [questId, setQuestId] = useState(null);
  // "Preview exact payload": asked of the server only while it is open — it
  // plans the requests the way a take is sent (see useTtsPreview).
  const [payloadOpen, setPayloadOpen] = useState(false);
  const [locationName, setLocationName] = useState('');
  const [locationOrder, setLocationOrder] = useState('');
  const [generatedAudio, setGeneratedAudio] = useState(null);
  const [truncatedWarning, setTruncatedWarning] = useState(null); // silence-watchdog cut
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); // { ok: bool, message: string }
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [isSplitting, setIsSplitting] = useState(false);
  const [splitInfo, setSplitInfo] = useState(null); // layout code from the voice-split agent
  const [splitError, setSplitError] = useState(null);
  const stressAbortRef = useRef(false); // silences a poll loop after view switch

  const [apiKey, setApiKey] = useState('');
  const [useOwnKey, setUseOwnKey] = useState(false);

  // Generation settings: last-used values survive reloads via localStorage.
  const [settings, setSettings] = useState(() => loadLastTtsSettings());
  const [presets, setPresets] = useState(() => loadTtsPresets());
  const [selectedPreset, setSelectedPreset] = useState('');
  const [presetName, setPresetName] = useState('');
  // The whole parameter panel starts folded — it is long and rarely touched
  // once a preset is picked, and it pushes the text area off the screen.
  const [paramsCollapsed, setParamsCollapsed] = useState(true);

  useEffect(() => {
    try {
      localStorage.setItem(LAST_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
      console.error('Failed to persist speech settings:', e);
    }
  }, [settings]);

  // A model picked by hand is pinned: no defaults change touches it later.
  const updateSetting = (key, value) => setSettings(prev => ({
    ...prev, [key]: value, ...(key === 'model' ? { modelPinned: true } : {}),
  }));

  // The body Save & Generate sends, planned by the server the way the take
  // will go out: one block per request, its voices, the prompt and the text
  // with the glossary's stress marks.
  const payload = useTtsPreview(payloadOpen && questId && text.trim()
    ? buildTtsRequest({ text, settings, questId })
    : null);
  let payloadText = 'Loading…';
  if (!questId) payloadText = 'No quest — nothing to plan the requests against.';
  else if (!text.trim()) payloadText = 'No text.';
  else if (payload.error) payloadText = `Could not get what will be sent: ${payload.error}`;
  else if (payload.data) {
    payloadText = payload.data.requests.map((r) => requestPreviewText(payload.data, r)).join('\n\n');
  }

  useEffect(() => {
    // When the component receives new initial data, update the state
    if (initialData) {
      setText(initialData.text || '');
      setLocationId(initialData.locationId);
      setLocationUuid(initialData.locationUuid);
      setField(initialData.field);
      setQuestId(initialData.questId || null);
      setLocationName(initialData.locationName || '');
      setLocationOrder(initialData.locationOrder || '');
    } else {
      setText('');
      setLocationId(null);
      setLocationUuid(null);
      setField(null);
      setQuestId(null);
      setLocationName('');
      setLocationOrder('');
    }
    // Reset audio and errors when the view is re-opened
    setGeneratedAudio(null);
    setTruncatedWarning(null);
    setUploadStatus(null);
    setError(null);
    setErrorDetails(null);
    stressAbortRef.current = true; // also silences a live generation poll loop
    setIsLoading(false);
    setIsSplitting(false);
    setSplitInfo(null);
    setSplitError(null);
  }, [initialData]);

  const insertTextAtCursor = (char) => {
    const textarea = document.querySelector('.speech-text-area');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newText = text.substring(0, start) + char + text.substring(end);
      setText(newText);
      // Move cursor after the inserted character
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + char.length;
        textarea.focus();
      }, 0);
    }
  };

  const handleBoldClick = () => {
    const textarea = document.querySelector('.speech-text-area');
    if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        if (start === end) return; // Do nothing if no text is selected

        const selectedText = text.substring(start, end);

        // Case 1: The user selected the text *and* the markers (e.g., "**hello**")
        if (selectedText.startsWith('**') && selectedText.endsWith('**')) {
            const unwrappedText = selectedText.substring(2, selectedText.length - 2);
            const newText = text.substring(0, start) + unwrappedText + text.substring(end);
            setText(newText);

            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start, start + unwrappedText.length);
            }, 0);
        }
        // Case 2: The user selected *only* the text inside the markers (e.g., "hello" from "**hello**")
        else if (text.substring(start - 2, start) === '**' && text.substring(end, end + 2) === '**') {
            const newText = text.substring(0, start - 2) + selectedText + text.substring(end + 2);
            setText(newText);

            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start - 2, start - 2 + selectedText.length);
            }, 0);
        }
        // Case 3: The text is not bold, so make it bold.
        else {
            const newText = `${text.substring(0, start)}**${selectedText}**${text.substring(end)}`;
            setText(newText);

            setTimeout(() => {
                textarea.focus();
                // Select the original text plus the markers
                textarea.setSelectionRange(start, end + 4);
            }, 0);
        }
    }
  };

  // ----- Presets ------------------------------------------------------------

  const persistPresets = (next) => {
    setPresets(next);
    try {
      localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(next));
    } catch (e) {
      console.error('Failed to persist presets:', e);
    }
  };

  const handlePresetSelect = (name) => {
    setSelectedPreset(name);
    setPresetName(name);
    if (name && presets[name]) {
      setSettings({ ...DEFAULT_SETTINGS, ...presets[name] });
    }
  };

  const handleSavePreset = () => {
    const name = presetName.trim();
    if (!name) return;
    persistPresets({ ...presets, [name]: settings });
    setSelectedPreset(name);
  };

  const handleDeletePreset = () => {
    if (!selectedPreset) return;
    const next = { ...presets };
    delete next[selectedPreset];
    persistPresets(next);
    setSelectedPreset('');
    setPresetName('');
  };

  const handleResetToDefaults = () => {
    setSettings(DEFAULT_SETTINGS);
    setSelectedPreset('');
    setPresetName('');
  };

  // Voice split: assigns Speaker 1/2 labels per paragraph (male/female by
  // content). Same start + poll flow as stress marks, just much faster.
  const handleVoiceSplitClick = async () => {
    if (!text.trim()) return;
    setIsSplitting(true);
    setSplitInfo(null);
    setSplitError(null);
    stressAbortRef.current = false;
    try {
      const startRes = await fetch('/api/voice-split', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text }),
      });
      const startData = await startRes.json().catch(() => ({}));
      if (!startRes.ok) throw new Error(startData.error || `HTTP ${startRes.status}`);

      const deadline = Date.now() + 10 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        if (stressAbortRef.current) return;
        const res = await fetch(`/api/voice-split/${startData.taskId}`, { credentials: 'include' });
        const task = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(task.error || `HTTP ${res.status}`);
        if (task.status === 'done') {
          setText(task.text);
          setSplitInfo(task.code || '');
          return;
        }
        if (task.status === 'error') throw new Error(task.error || 'Voice split failed');
      }
      throw new Error('Timed out waiting for the voice split');
    } catch (err) {
      console.error('Voice split error:', err);
      if (!stressAbortRef.current) setSplitError(err.message);
    } finally {
      if (!stressAbortRef.current) setIsSplitting(false);
    }
  };

  // ----- Save / generate / upload -------------------------------------------

  const handleSaveClick = async () => {
    if (onSaveVoiceoverText && locationId && field) {
      setIsSaving(true);
      try {
        // Assuming onSaveVoiceoverText might be async, we await it.
        await onSaveVoiceoverText(locationId, field, text);
      } catch (err) {
        console.error('Error saving text:', err);
        setError('Failed to save text.');
      } finally {
        setIsSaving(false);
      }
    }
  };

  // Start + poll, same shape as stress marks. The audio is generated and
  // stored server-side as the new ACTIVE voice-over version, so a connection
  // blip mid-generation no longer loses the result — polling just resumes.
  const handleGenerateClick = async () => {
    // First, save the current text to the corresponding _voiceover field
    await handleSaveClick();

    setIsLoading(true);
    setError(null);
    setErrorDetails(null);
    setGeneratedAudio(null);
    setTruncatedWarning(null);
    setUploadStatus(null);
    stressAbortRef.current = false;

    try {
      const startRes = await fetch('/api/generate-speech-task', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(buildTtsRequest({
          text, settings, apiKey, locationUuid, fieldType: field, questId,
        })),
      });
      const startData = await startRes.json().catch(() => ({}));
      if (!startRes.ok) {
        setErrorDetails(startData.details || null);
        throw new Error(startData.error || `Failed to start generation (HTTP ${startRes.status})`);
      }

      const deadline = Date.now() + 20 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        if (stressAbortRef.current) return;

        // A failed poll (connection blip) is not fatal — just try again.
        let res;
        try {
          res = await fetch(`/api/generate-speech-task/${startData.taskId}`, { credentials: 'include' });
        } catch (pollErr) {
          console.warn('Speech status poll failed, retrying:', pollErr.message);
          continue;
        }
        const task = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(task.error || `HTTP ${res.status}`);

        if (task.status === 'done') {
          if (task.truncatedBySilence) {
            setTruncatedWarning(
              `The model drifted into silence and was stopped after 10s of it. `
              + `Voiced audio: ~${task.voicedSec || '?'}s — the tail of the text is probably NOT voiced. `
              + `The version is stored anyway; listen to where it ends and regenerate if needed.`
            );
          }
          // The new version is already stored and active — play it from the DB.
          setGeneratedAudio(
            `/api/audio/${locationId}/${field}?uuid=${locationUuid}&questId=${questId}`
            + `&table=quests_test&versionId=${task.versionId}&t=${Date.now()}`
          );
          setUploadStatus({ ok: true, message: `Stored as version ${task.versionId} (now active)` });
          if (onAudioUploaded) onAudioUploaded();
          return;
        }
        if (task.status === 'error') {
          setErrorDetails(task.details || null);
          throw new Error(task.error || 'Speech generation failed');
        }
      }
      throw new Error('Timed out waiting for speech generation (20 min)');
    } catch (err) {
      console.error('Speech generation error:', err);
      if (!stressAbortRef.current) setError(err.message);
    } finally {
      if (!stressAbortRef.current) setIsLoading(false);
      notifyTtsAttempt();
    }
  };

  return (
    <div className="speech-generation-container">
      <div className="speech-generation-header">
        <h2>Create Voiceover</h2>
        <LinkButton href={backHref} onClick={onBack} className="back-button">Back to Editor</LinkButton>
      </div>

      <div className="speech-generation-info">
        Editing voiceover for: <strong>{locationOrder}. {locationName}</strong> (<code>{field}</code>)
      </div>

      <div className="speech-generation-controls">
        {/* Text Formatting Controls */}
        <div className="text-format-controls">
            <button onClick={handleBoldClick} className="format-button"><b>B</b></button>
        </div>

        {/* Transcript snippets (speaker labels, delivery tags) */}
        <div className="speech-param-row accent-chars">
            <label>Insert:</label>
            {insertSnippets.map(snippet => (
                <button key={snippet} onClick={() => insertTextAtCursor(snippet)} className="accent-char-button snippet-button" title={`Insert '${snippet}'`}>
                    {snippet.trim()}
                </button>
            ))}
        </div>

        {/* Accent Characters */}
        <div className="speech-param-row accent-chars">
            <label>Accents:</label>
            {accentChars.map(char => (
                <button key={char} onClick={() => insertTextAtCursor(char)} className="accent-char-button" title={`Copy '${char}'`}>
                    {char}
                </button>
            ))}
        </div>

        {/* Emoji Characters */}
        <div className="speech-param-row accent-chars">
            <label>Emojis:</label>
            {emojiChars.map(char => (
                <button key={char} onClick={() => insertTextAtCursor(char)} className="accent-char-button emoji-button" title={`Insert '${char}'`}>
                    {char}
                </button>
            ))}
        </div>

        {/* Text Area */}
        <textarea
          className="speech-text-area"
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Text is loaded automatically. You can edit it here before generation."
        />
        <div className="speech-newline-hint">
          Line breaks inside a speaker's turn are removed before generation (they cause long pauses).
          Keep 3+ empty lines in a row to force an intentional pause.
        </div>

        {/* Generation Parameters */}
        <div className="speech-parameters">
            <button
                type="button"
                className="speech-params-toggle"
                aria-expanded={!paramsCollapsed}
                onClick={() => setParamsCollapsed(c => !c)}
            >
                <span className="speech-params-caret">{paramsCollapsed ? '▸' : '▾'}</span>
                Generation Parameters
            </button>
            {!paramsCollapsed && (<>
            <div className="speech-param-row">
                <label>Preset:</label>
                <select value={selectedPreset} onChange={e => handlePresetSelect(e.target.value)}>
                    <option value="">— none —</option>
                    {Object.keys(presets).sort().map(name => (
                        <option key={name} value={name}>{name}</option>
                    ))}
                </select>
                <input
                    type="text"
                    placeholder="Preset name"
                    value={presetName}
                    onChange={e => setPresetName(e.target.value)}
                />
                <button onClick={handleSavePreset} disabled={!presetName.trim()} className="preset-button" title="Save current settings under this name">Save</button>
                <button onClick={handleDeletePreset} disabled={!selectedPreset} className="preset-button" title="Delete selected preset">Delete</button>
                <button onClick={handleResetToDefaults} className="preset-button" title="Reset all settings to AI Studio defaults">Defaults</button>
            </div>
            <div className="speech-param-row">
                <label>API Key:</label>
                <select
                    value={useOwnKey ? 'own' : 'pool'}
                    onChange={e => {
                        const own = e.target.value === 'own';
                        setUseOwnKey(own);
                        if (!own) setApiKey('');
                    }}
                >
                    <option value="pool">Server keys (rotated; a limited one rests)</option>
                    <option value="own">My own key…</option>
                </select>
                {useOwnKey && (
                    <input
                        type="password"
                        value={apiKey}
                        onChange={e => setApiKey(e.target.value)}
                        placeholder="paste a Gemini API key"
                        style={{ marginLeft: 8, width: 260 }}
                    />
                )}
            </div>
            <div className="speech-param-row">
                <label>Model:</label>
                <input type="text" list="tts-models" value={settings.model} onChange={e => updateSetting('model', e.target.value)} />
                <datalist id="tts-models">
                    {TTS_MODELS.map(m => <option key={m} value={m} />)}
                </datalist>
            </div>
            <div className="speech-param-row">
                <label>Scheme:</label>
                <select value={settings.scheme === 'whole' ? 'whole' : 'turns'} onChange={e => updateSetting('scheme', e.target.value)}>
                    {TTS_SCHEMES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
            </div>
            <div className="speech-param-row">
                <label>Temperature:</label>
                <input type="range" min="0" max="2" step="0.05" value={settings.temperature} onChange={e => updateSetting('temperature', parseFloat(e.target.value))} />
                <span>{Number(settings.temperature).toFixed(2)}</span>
            </div>
            <div className="speech-param-row">
                <label>Voice Sp.1:</label>
                <input type="text" list="prebuilt-voices" value={settings.voice1} onChange={e => updateSetting('voice1', e.target.value)} />
                <label>Voice Sp.2:</label>
                <input type="text" list="prebuilt-voices" value={settings.voice2} onChange={e => updateSetting('voice2', e.target.value)} />
                <datalist id="prebuilt-voices">
                    {PREBUILT_VOICES.map(v => <option key={v} value={v} />)}
                </datalist>
            </div>

            <h4>Audio Profile</h4>
            <div className="speech-param-row">
                <label>Speaker 1:</label>
                <textarea value={settings.profile1} onChange={e => updateSetting('profile1', e.target.value)} />
            </div>
            <div className="speech-param-row">
                <label>Speaker 2:</label>
                <textarea value={settings.profile2} onChange={e => updateSetting('profile2', e.target.value)} />
            </div>

            <h4>Director's Note</h4>
            <div className="speech-param-row">
                <label>Speaker 1:</label>
                <textarea value={settings.note1} onChange={e => updateSetting('note1', e.target.value)} />
            </div>
            <div className="speech-param-row">
                <label>Speaker 2:</label>
                <textarea value={settings.note2} onChange={e => updateSetting('note2', e.target.value)} />
            </div>

            <h4>Scene &amp; Context</h4>
            <div className="speech-param-row">
                <label>Scene:</label>
                <textarea value={settings.scene} onChange={e => updateSetting('scene', e.target.value)} />
            </div>
            <div className="speech-param-row">
                <label>Sample Context:</label>
                <textarea value={settings.sampleContext} onChange={e => updateSetting('sampleContext', e.target.value)} />
            </div>

            <details className="speech-prompt-preview" onToggle={e => setPayloadOpen(e.currentTarget.open)}>
                <summary>Preview exact payload sent to the model</summary>
                <pre>{payloadText}</pre>
            </details>
            </>)}
        </div>

        {/* Two groups: tools that rewrite the text on the left, saving on the
            right. Only Save & Generate is filled — it is the one that spends
            quota — and it is marked by fill, weight and ▶, never by hue alone.
            Labels that change while busy keep the button's width (StableLabel). */}
        <div className="speech-generation-actions">
          <div className="sg-group">
            <button
              onClick={handleVoiceSplitClick}
              disabled={isLoading || isSaving || isSplitting || !text}
              className="sg-btn"
              title={`Assign Speaker 1/2 (male/female) labels per paragraph by content — the text must not have Speaker labels yet. Model: ${modelKey(pipelineModels, 'voicesplit') || '…'}`}
            >
              <StableLabel current={isSplitting ? 'Assigning voices…' : '🎭 Assign voices'}
                           sizes={['🎭 Assign voices', 'Assigning voices…']} />
            </button>
            <ModelPicker k="voicesplit" />
            <PromptLink k="voicesplit" />
          </div>
          <div className="sg-group sg-group-end">
            <button onClick={handleSaveClick} disabled={isLoading || isSaving} className="sg-btn">
              <StableLabel current={isSaving ? 'Saving…' : 'Save text'} sizes={['Save text', 'Saving…']} />
            </button>
            <button onClick={handleGenerateClick} disabled={isLoading || isSaving || !text} className="sg-btn sg-btn-primary">
              <StableLabel current={isLoading ? 'Generating…' : '▶ Save & Generate'}
                           sizes={['▶ Save & Generate', 'Generating…']} />
            </button>
          </div>
        </div>

        {splitError && (
          <div className="upload-status upload-status-error">✗ {splitError}</div>
        )}
        {splitInfo !== null && !isSplitting && (
          <div className="stress-notes">🎭 Voices assigned ({splitInfo}). Review the labels and hit Save Text.</div>
        )}
        {questId && (
          <div className="stress-notes">
            📖 Stress: the quest glossary is applied when the text is voiced — no need to stamp it here.
            A mark typed into this text stresses that one place its own way, whatever the glossary says.
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
            <strong>{error}</strong>
            {errorDetails && (
                <pre className="error-details">
                    {typeof errorDetails === 'object' ? JSON.stringify(errorDetails, null, 2) : errorDetails}
                </pre>
            )}
        </div>
      )}

      {generatedAudio && (
        <div className="speech-generation-result">
          <h3>Generated Audio</h3>
          {truncatedWarning && (
            <div className="upload-status upload-status-error">⚠ {truncatedWarning}</div>
          )}
          <audio controls src={generatedAudio} />
          <div className="speech-result-actions">
            <a
              href={generatedAudio}
              download={locationUuid && field ? `${locationUuid}_${field}.ogg` : 'generated_speech.ogg'}
              className="download-button"
            >
              Download
            </a>
          </div>
          {uploadStatus && (
            <div className={`upload-status ${uploadStatus.ok ? 'upload-status-ok' : 'upload-status-error'}`}>
              {uploadStatus.ok ? '✓' : '✗'} {uploadStatus.message}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SpeechGeneration;